import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogDetails,
  getRecentBlogs,
} from "../../../../services/blog";
import { blog, blogInner } from "../../../../../images";
import Testimonial from "../Testimonials/Testimonial";
import SEO from "../../../../SEO/Seo";
import { metaContent } from "../../../../SEO/metaContent";
import BlogCard from "./BlogCard";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blogD, setBlogD] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);

  // Layout refs and state
  const contentRef = useRef(null);
  const recentBlogsRef = useRef(null);
  const [sidebarImageCount, setSidebarImageCount] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate how many gallery images fit in the sidebar
  useEffect(() => {
    if (!contentRef.current || !blogD?.gallery_images?.length) return;

    const calculateSidebarImages = () => {
      if (window.innerWidth < 992) {
        setSidebarImageCount(blogD.gallery_images.length);
        return;
      }
      
      const contentHeight = contentRef.current.offsetHeight;
      const recentHeight = recentBlogsRef.current ? recentBlogsRef.current.offsetHeight : 0;
      
      // Calculate available space in the sidebar (deducting approx margins)
      const spaceForImages = contentHeight - recentHeight - 48; 
      // Lowered estimate to 250px and use Math.round to fill the gap better
      // even if the last image slightly overflows the text boundary.
      const estimatedImageHeight = 250; 
      
      let count = Math.round(spaceForImages / estimatedImageHeight);
      if (count < 0) count = 0;
      if (count > blogD.gallery_images.length) count = blogD.gallery_images.length;
      
      setSidebarImageCount(count);
    };

    const resizeObserver = new ResizeObserver(calculateSidebarImages);
    resizeObserver.observe(contentRef.current);
    if (recentBlogsRef.current) resizeObserver.observe(recentBlogsRef.current);
    
    return () => resizeObserver.disconnect();
  }, [blogD, recentBlogs]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogDetails();
    fetchRecentBlogs();
  }, [id]);

  const fetchBlogDetails = async () => {
    try {
      const response = await getBlogDetails(id);

      if (response?.success) {
        setBlogD(response.data);
      }
    } catch (error) {
      console.error("Error fetching blog details:", error);
    }
  };

  const fetchRecentBlogs = async () => {
    try {
      const response = await getRecentBlogs(id);

      if (response?.success) {
        setRecentBlogs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching recent blogs:", error);
    }
  };

  useEffect(() => {
    const staticContainer = document.querySelector(".static-container");
    if (!staticContainer) return;

    const oldBackground = staticContainer.style.backgroundImage;
    const oldBgColor = staticContainer.style.backgroundColor;
    const oldBgSize = staticContainer.style.backgroundSize;
    const oldBgRepeat = staticContainer.style.backgroundRepeat;
    const oldBgPosition = staticContainer.style.backgroundPosition;
    const oldBgAttachment = staticContainer.style.backgroundAttachment;

    staticContainer.style.backgroundImage = `url(${blogInner})`;
    staticContainer.style.backgroundSize = "cover";
    staticContainer.style.backgroundRepeat = "no-repeat";
    staticContainer.style.backgroundPosition = "center";
    staticContainer.style.transition = "background 0.5s ease-in-out";

    return () => {
      staticContainer.style.backgroundImage = oldBackground;
      staticContainer.style.backgroundColor = oldBgColor;
      staticContainer.style.backgroundSize = oldBgSize;
      staticContainer.style.backgroundRepeat = oldBgRepeat;
      staticContainer.style.backgroundPosition = oldBgPosition;
      staticContainer.style.backgroundAttachment = oldBgAttachment;
    };
  }, []);

  if (!blogD) {
    return (
      <div className="text-center py-5">
        <p>Loading...</p>
      </div>
    );
  }

  const seoData = metaContent.blogDetails(
    id,
    blogD.title,
    blogD.short_description,
    blogD.featured_image
  );

  const sidebarImages = blogD?.gallery_images?.slice(
    0, 
    isDesktop ? sidebarImageCount : blogD.gallery_images.length
  ) || [];
  
  const bottomImages = isDesktop && blogD?.gallery_images 
    ? blogD.gallery_images.slice(sidebarImageCount) 
    : [];

  return (
    <>
      <SEO {...seoData} />

      <section className="chaos-section">
        <div className="chaos-container">
          <div className="chaos-content">
            <div className="row align-items-center mt-3 pt-3">
              <div className="col-12 col-md-8 col-lg-8">
                <h2 className="hero-heading gradient-text mt-0">
                  {blogD.title}
                </h2>

                <p className="hero-sub-text mt-2">
                  {blogD.short_description}
                </p>

                <p className="text-white mt-1">
                  Published: {blogD.published_at}
                </p>
              </div>

              <div className="col-12 col-md-4 col-lg-4">
                <div>
                  {blogD.featured_image && (
                    <img
                      src={blogD.featured_image}
                      alt={blogD.title}
                      className="img-fluid rounded"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              {/* Blog Content */}
              <div className="col-12 col-md-12 col-lg-8 mt-4 text-white">
                <div 
                  ref={contentRef}
                  className="chaos-body"
                  dangerouslySetInnerHTML={{ __html: blogD.content?.body || "" }} 
                />
              </div>

              {/* Sidebar: Recent Blogs and Gallery Images */}
              <div className="col-12 col-md-12 col-lg-4 mt-5">
                <div ref={recentBlogsRef}>
                  {recentBlogs?.slice(0, 2).map((blog) => (
                    <BlogCard
                      key={blog.id}
                      blog={blog}
                      onClick={() =>
                        navigate(`/blog-details/${blog.id}`)
                      }
                    />
                  ))}
                </div>

                {/* Sidebar Gallery Images */}
                {sidebarImages.length > 0 && (
                  <div className="row mt-5 pt-4">
                    {sidebarImages.map((imgUrl, index) => (
                      <div key={index} className="col-12 mb-5">
                        <img src={imgUrl} alt={`Gallery ${index}`} className="img-fluid rounded w-100" style={{ objectFit: "cover" }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Gallery Images (Full Width Grid) */}
            {bottomImages.length > 0 && (
              <div className="row mt-5 pt-3">
                {bottomImages.map((imgUrl, index) => (
                  <div key={`bottom-${index}`} className="col-12 col-md-6 col-lg-4 mb-4">
                    <img 
                      src={imgUrl} 
                      alt={`Gallery Bottom ${index}`} 
                      className="img-fluid rounded w-100" 
                      style={{ objectFit: "cover", height: "250px" }} 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Testimonial />
    </>
  );
};

export default BlogDetails;